import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FloatingTree } from '@floating-ui/react';
import { useMediaQuery } from '@uidotdev/usehooks';
import { useLiveQuery } from 'dexie-react-hooks';
import { Router } from 'wouter';

import { apiPostAsUser } from '../lib/api_v2';
import { getChannelDisplayName } from '../lib/channels';
import db, { DEXIE_IS_LOADING } from '../lib/db';
import { assignDocument } from '../lib/desktop_navigation_diagnostics';
import { logError } from '../lib/logger';
import {
  appLinkUrl,
  currentChannelShareKey,
  currentShadowId,
  currentUserId,
  currentWorkspaceId,
  getMetaContent,
  readSharedShellBootstrap,
  rootDomainUrl,
} from '../lib/meta';
import { isIOSDevice } from '../lib/platform';
import { cn } from '../lib/utils';

import NavGridIcon from '../../assets/images/nav-grid.svg';

import { EntitySnapshotContext, buildEntitySnapshots } from '../contexts/EntitySnapshotContext';
import { KeyMasterProvider } from '../contexts/KeyMaster';
import { RichdownComposerFocusRouterProvider } from '../contexts/RichdownComposerFocusRouter';
import { RouteWorkspaceProvider, useRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import useComposerDropzone from '../hooks/useComposerDropzone';
import { useDocumentTitleContext } from '../hooks/useDocumentTitleEvents';
import { useLayoutPreferences } from '../hooks/useLayoutPreferences';
import { useMobileSnapPanels } from '../hooks/useMobileSnapPanels';
import MessageFeed from '../feed/ui/MessageFeed';
import SharedPageFooter from './SharedPageFooter';
import UnreadManager from './UnreadManager';
import WorkspacePresenceManager from './WorkspacePresenceManager';
import { LayoutHeaderNavButton as NavButton } from './layouts/LayoutHeaderControls';
import {
  BREAKPOINT_SM,
  CloseWidgetsButton,
  LayoutNavBar,
  MainLayoutFrame,
  useAuthenticatedShellRailState,
  useCurrentUserShell,
} from './layouts/MainLayout';
import BoardView from './layouts/views/BoardView';
import DropOverlay from './messages/DropOverlay';
import LoadingWrapper from './shared/LoadingWrapper';
import {
  WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
  WorkspaceBackgroundImageLayer,
  useWorkspaceBackgroundImage,
} from './shared/WorkspaceBackgroundImage';

const SHARED_CHANNEL_ENTITY_SNAPSHOT_COLLECTIONS = ['profiles', 'channels', 'emojis'];
const SHARED_PAGE_FOOTER_DESKTOP_ESTIMATED_HEIGHT = 96;
const SHARED_PAGE_FOOTER_MOBILE_ESTIMATED_HEIGHT = 308;

const sharedChannelEntitySnapshotScope = (workspaceId, channelId) => `${workspaceId}:${channelId}`;

const sharedChannelEntitySnapshotKeys = (workspaceId, channelId) =>
  SHARED_CHANNEL_ENTITY_SNAPSHOT_COLLECTIONS.map(
    (collection) => `${collection}:${sharedChannelEntitySnapshotScope(workspaceId, channelId)}`,
  );

// Fixed-width widgets pane, mirroring MainLayout's non-resizable widgets pane.
const WIDGETS_PANE_WIDTH = 352;

const ChatWrapper = ({
  currentChannel,
  dropzoneHelpers,
  handleDropzoneStateChange,
  handleUploadCountChange,
  maxMediaAttachments,
  maxRichdownContentSize,
  bottomOverlayHeight = 0,
}) => {
  const { workspaceId } = useRouteWorkspace();
  const channelId = currentChannel?.id || null;
  const entitySnapshotScope = workspaceId && channelId ? sharedChannelEntitySnapshotScope(workspaceId, channelId) : null;
  const [bootstrappedEntitySnapshotScope, setBootstrappedEntitySnapshotScope] = useState(null);
  const entitySnapshotState = useLiveQuery(
    async () => {
      if (!workspaceId) {
        const emojis = await db.emojis.where('workspace_id').equals('system').toArray();
        return { ready: true, snapshots: buildEntitySnapshots(null, [], [], emojis) };
      }
      if (!channelId) return DEXIE_IS_LOADING;

      const [profiles, channels, emojis, fingerprints] = await Promise.all([
        db.profiles.where('workspace_id').equals(workspaceId).toArray(),
        db.channels.where('workspace_id').equals(workspaceId).toArray(),
        db.emojis.where('workspace_id').anyOf(['system', workspaceId]).toArray(),
        db._fingerprints.bulkGet(sharedChannelEntitySnapshotKeys(workspaceId, channelId)),
      ]);
      const ready = fingerprints.every(Boolean);
      return { ready, snapshots: buildEntitySnapshots(workspaceId, profiles, channels, emojis) };
    },
    [channelId, workspaceId],
    DEXIE_IS_LOADING,
  );

  useEffect(() => {
    if (entitySnapshotState === DEXIE_IS_LOADING || !entitySnapshotState?.ready) return;
    setBootstrappedEntitySnapshotScope(entitySnapshotScope);
  }, [entitySnapshotScope, entitySnapshotState]);

  const hasBootstrappedEntitySnapshots = bootstrappedEntitySnapshotScope === entitySnapshotScope;
  if (!hasBootstrappedEntitySnapshots || entitySnapshotState === DEXIE_IS_LOADING) {
    return <LoadingWrapper interactive={false} />;
  }

  const entitySnapshots = entitySnapshotState?.snapshots || null;

  return (
    <FloatingTree>
      <EntitySnapshotContext.Provider value={entitySnapshots}>
        <RichdownComposerFocusRouterProvider>
          <div className="@container size-full">
            <div className="h-full">
              <MessageFeed
                currentChannel={currentChannel}
                dropzoneHelpers={dropzoneHelpers}
                handleDropzoneStateChange={handleDropzoneStateChange}
                handleUploadCountChange={handleUploadCountChange}
                isSharedChannel
                maxMediaAttachments={maxMediaAttachments}
                maxRichdownContentSize={maxRichdownContentSize}
                bottomOverlayHeight={bottomOverlayHeight}
              />
            </div>
          </div>
        </RichdownComposerFocusRouterProvider>
      </EntitySnapshotContext.Provider>
    </FloatingTree>
  );
};

const SharedChannelAppInner = ({ channelId, shadowId, withMainLayoutShell = false }) => {
  const { setTitle, setUnreadIndicator } = useDocumentTitleContext();
  const { workspaceId, workspace: currentWorkspace, profileId } = useRouteWorkspace();

  // load up all the channel and related config when the channelId changes
  const currentConfig = useLiveQuery(
    async () => {
      // if no channelId provided, wait (will be set via navigation)
      if (!channelId) return DEXIE_IS_LOADING;
      if (!workspaceId) return DEXIE_IS_LOADING;

      const channel = await db.channels.get([workspaceId, channelId]);

      // no channel in dexie? just wait for the collection to initialize
      if (!channel) return DEXIE_IS_LOADING;

      // the shared channel only has access to the profiles that have posted in the channel so we can just fetch all of them
      const profiles = await db.profiles.where('workspace_id').equals(workspaceId).toArray();

      return { profiles, channel };
    },
    [channelId, workspaceId],
    DEXIE_IS_LOADING,
  );

  // Update document title when channel changes
  useEffect(() => {
    if (currentConfig === DEXIE_IS_LOADING || !currentConfig?.channel) return;
    setTitle(currentConfig.channel.name || 'Untitled');
  }, [currentConfig, setTitle]);

  // kick to root if the workspace is deactivated
  useEffect(() => {
    if (!currentWorkspace?.slug?.startsWith('__deactivated__')) return;
    window.dispatchEvent(new CustomEvent('output:kill-workspace', { detail: { workspaceId: currentWorkspace.id } }));
  }, [currentWorkspace?.id, currentWorkspace?.slug]);

  // mobile layout state
  const isBreakpointSm = useMediaQuery(BREAKPOINT_SM);
  const mobileSidebarPaneRef = useRef(null);
  const [mobileSidebarInView, setMobileSidebarInView] = useState(false);
  const { setScrollContainerRef, scrollContainerRef } = useMobileSnapPanels({
    isEnabled: isBreakpointSm,
    getPanelIndex,
    initialPanel: 'main',
  });

  const isInteractive = useMemo(() => {
    if (!currentConfig?.channel?.kind) return false;
    return ['public', 'open_breakout'].includes(currentConfig.channel.kind);
  }, [currentConfig?.channel?.kind]);

  const { settings: layoutSettings, setLayoutPref } = useLayoutPreferences();
  const isShadowViewer = useMemo(() => Boolean(shadowId) && !profileId, [profileId, shadowId]);
  const presenceDisabled = Boolean(shadowId);
  const isSignedInShadowViewer = isShadowViewer && withMainLayoutShell;
  const { hasWorkspaceBackgroundImage, workspaceBackgroundImageStyle } = useWorkspaceBackgroundImage(currentWorkspace);
  const [sharedFooterHeight, setSharedFooterHeight] = useState(0);
  const sharedFooterContainerRef = useRef(null);
  const effectiveSharedFooterHeight = useMemo(() => {
    if (!isShadowViewer) return 0;

    const estimatedHeight = isBreakpointSm ? SHARED_PAGE_FOOTER_MOBILE_ESTIMATED_HEIGHT : SHARED_PAGE_FOOTER_DESKTOP_ESTIMATED_HEIGHT;
    return Math.max(sharedFooterHeight, estimatedHeight);
  }, [isBreakpointSm, isShadowViewer, sharedFooterHeight]);

  // Desktop-only: the widgets pane collapses/expands via the shared `widgetsOpen` layout pref,
  // mirroring MainLayout. Mobile show/hide is driven by the snap-panel scroll handlers below.
  const handleToggleWidgets = useCallback(
    function handleToggleWidgetsCallback() {
      setLayoutPref('widgetsOpen', (prev) => !prev);
    },
    [setLayoutPref],
  );

  const handleCloseWidgets = useCallback(
    function handleCloseWidgetsCallback() {
      setLayoutPref('widgetsOpen', false);
    },
    [setLayoutPref],
  );

  const handleToggleSidebar = useCallback(
    function toggleSidebar() {
      const container = scrollContainerRef.current;
      const sidebarPane = mobileSidebarPaneRef.current;
      if (!container || !sidebarPane) return;
      container.scrollTo({
        left: mobileSidebarInView ? 0 : sidebarPane.offsetLeft,
        behavior: 'smooth',
      });
    },
    [mobileSidebarInView, scrollContainerRef],
  );

  const handleScrollToMain = useCallback(
    function scrollToMain() {
      const container = scrollContainerRef.current;
      if (!container) return;
      container.scrollTo({ left: 0, behavior: 'smooth' });
    },
    [scrollContainerRef],
  );

  // On iOS, route signed-out visitors through the universal-link host so the
  // tap opens the app when installed (universal links never fire for
  // same-host links, so the page's own /chat/:share_key URL can't do this)
  // and falls back to the App Store when it isn't. Signed-in web users keep
  // the normal one-tap web join instead. Returns true when the redirect was
  // issued.
  const redirectToAppLink = useCallback(function redirectToAppLinkCallback() {
    if (!isIOSDevice()) return false;
    const shareKey = currentChannelShareKey();
    if (!shareKey) return false;
    window.location.assign(appLinkUrl(`/chat/${encodeURIComponent(shareKey)}`));
    return true;
  }, []);

  const handleLoginClick = useCallback(
    function handleLoginClickCallback() {
      if (redirectToAppLink()) return;
      window.location.assign(rootDomainUrl('/login'));
    },
    [redirectToAppLink],
  );

  const handleJoinWorkspaceClick = useCallback(
    async function handleJoinWorkspaceClickCallback() {
      const shareKey = currentChannelShareKey();
      if (!shareKey || !shadowId) {
        assignDocument(rootDomainUrl('/app/start'), 'workspace-switch');
        return;
      }

      try {
        const response = await apiPostAsUser(`/shared_channels/${encodeURIComponent(shareKey)}/join`, { shadow_id: shadowId });
        const data = await response.json();
        if (!response.ok || !data.ok) {
          logError('SharedChannelApp', 'Failed to join shared channel', { errors: data.errors, validations: data.validations });
        }
        assignDocument(data.data?.redirect_url || rootDomainUrl('/app/start'), 'workspace-switch');
      } catch (error) {
        logError('SharedChannelApp', 'Failed to join shared channel', { error });
        assignDocument(rootDomainUrl('/app/start'), 'workspace-switch');
      }
    },
    [shadowId],
  );

  const MESSAGE_MAX_MEDIA_ATTACHMENTS = useMemo(() => parseInt(getMetaContent('message:max_media_attachments')), []);
  const MESSAGE_MAX_RICHDOWN_CONTENT_SIZE = useMemo(() => parseInt(getMetaContent('message:max_richdown_content_size')), []);

  const { dropzoneHelpers, getRootProps, handleDropzoneStateChange, handleUploadCountChange, showDropzoneOverlay } = useComposerDropzone({
    isInteractive,
  });

  // Unread counts handler
  const handleOutputUnreadCounts = useCallback(
    async function handleOutputUnreadCountsCallback(event) {
      if (!event.detail || !Array.isArray(event.detail.unreadCounts)) return;

      // Calculate the unread indicator
      const mentionsCount = event.detail.unreadCounts.reduce((sum, data) => sum + (data.mentions || 0), 0);
      const messagesCount = event.detail.unreadCounts.reduce((sum, data) => sum + (data.messages || 0), 0);

      const indicator = mentionsCount > 0 ? mentionsCount : messagesCount > 0 ? '•' : '';
      setUnreadIndicator(indicator);

      // forward the raw event detail to electron if available
      if (!window.electron?.events || !window.electron?.events['output:unread-counts']) return;
      await window.electron.events['output:unread-counts'](event.detail);
    },
    [setUnreadIndicator],
  );

  // attach window handlers for chat-specific events
  useEffect(() => {
    window.addEventListener('output:unread-counts', handleOutputUnreadCounts);

    return () => {
      window.removeEventListener('output:unread-counts', handleOutputUnreadCounts);
    };
  }, [handleOutputUnreadCounts]);

  useEffect(() => {
    if (!isShadowViewer) {
      setSharedFooterHeight(0);
      return;
    }

    const container = sharedFooterContainerRef.current;
    if (!container) return;

    const updateSharedFooterHeight = (height) => {
      const nextHeight = Math.ceil(height);
      setSharedFooterHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };

    updateSharedFooterHeight(container.getBoundingClientRect().height);

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        updateSharedFooterHeight(entry.target.getBoundingClientRect().height);
      });
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [isShadowViewer]);

  useEffect(() => {
    const forwardNagbarToReload = (event) => {
      window.dispatchEvent(
        new CustomEvent('output:reload', {
          detail: { source: 'shared-channel', reason: 'nagbar-forwarded', nagbarReason: event.detail?.reason },
        }),
      );
    };
    window.addEventListener('output:internal:nagbar', forwardNagbarToReload);
    return () => window.removeEventListener('output:internal:nagbar', forwardNagbarToReload);
  }, []);

  useEffect(() => {
    if (!isBreakpointSm) return;
    const container = scrollContainerRef.current;
    const target = mobileSidebarPaneRef.current;
    if (!container || !target) return;

    const observer = new IntersectionObserver(
      function handleSidebarPaneIntersection(entries) {
        setMobileSidebarInView(entries[0].isIntersecting);
      },
      { root: container, threshold: 0.95 },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isBreakpointSm, scrollContainerRef]);

  if (currentConfig === DEXIE_IS_LOADING) return <LoadingWrapper fullscreen />;

  const sidebarContent = (
    <div
      className={cn(
        'relative flex size-full flex-col overflow-hidden',
        hasWorkspaceBackgroundImage ? 'bg-transparent' : 'bg-gray-200',
        hasWorkspaceBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
      )}
      data-workspace-id={currentWorkspace?.id || ''}
      data-workspace-slug={currentWorkspace?.slug || ''}
    >
      {hasWorkspaceBackgroundImage ? (
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0 bg-black/30 backdrop-blur-[12px]" />
      ) : null}
      <LayoutNavBar withBackdrop backdropClassName="h-[74px]" className="z-20 justify-end">
        {isBreakpointSm ? (
          <NavButton onClick={handleScrollToMain} active={false} title="Back to chat" onImage={hasWorkspaceBackgroundImage}>
            <NavGridIcon className="size-4" />
          </NavButton>
        ) : (
          <CloseWidgetsButton onClick={handleCloseWidgets} onImage={hasWorkspaceBackgroundImage} />
        )}
      </LayoutNavBar>
      <div
        className={cn(
          'scrollbar-thin relative isolate z-0 min-h-0 flex-1 overflow-y-auto border-l-[0.5px] pt-[76px]',
          hasWorkspaceBackgroundImage ? 'border-white/10 bg-transparent' : 'border-gray-200 bg-gray-200',
        )}
      >
        {/* `viewerUnavailableReason="shadow"` is mandatory here: the shared page must never mint
            widget viewer sessions, even for a workspace member who opens the public share link
            (RouteWorkspaceProvider can still resolve their profile under requireProfile={false}). */}
        <BoardView interactive={false} viewerUnavailableReason="shadow" />
      </div>
    </div>
  );

  const renderMainContent = (className) => (
    <section {...getRootProps({ className })}>
      <DropOverlay isVisible={showDropzoneOverlay} label={`Upload to ${getChannelDisplayName(currentConfig.channel)}`} />

      {isBreakpointSm && (
        <button
          type="button"
          onClick={handleToggleSidebar}
          title="Sidebar"
          aria-label="Sidebar"
          className="absolute top-3.5 right-3 z-(--stack-sidebar-header-mobile) flex size-7 cursor-pointer items-center justify-center rounded p-1.5 text-gray-500 hover:bg-gray-200 dark:text-neutral-400 dark:hover:bg-neutral-700"
        >
          <NavGridIcon className="size-4" />
        </button>
      )}

      {!isBreakpointSm && (
        <div className="absolute top-5 right-5 z-10">
          <NavButton
            onClick={handleToggleWidgets}
            active={layoutSettings.widgetsOpen}
            title="Board"
            onImage={hasWorkspaceBackgroundImage}
            aria-hidden={layoutSettings.widgetsOpen}
            tabIndex={layoutSettings.widgetsOpen ? -1 : undefined}
            className={cn(
              'transition-opacity duration-150 ease-fluid',
              layoutSettings.widgetsOpen ? 'pointer-events-none opacity-0 delay-75' : 'opacity-100 delay-0',
            )}
          >
            <NavGridIcon className="size-4" />
          </NavButton>
        </div>
      )}

      <div className="relative mx-auto flex size-full min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col">
          <ChatWrapper
            currentChannel={currentConfig.channel}
            dropzoneHelpers={dropzoneHelpers}
            handleDropzoneStateChange={handleDropzoneStateChange}
            handleUploadCountChange={handleUploadCountChange}
            maxMediaAttachments={MESSAGE_MAX_MEDIA_ATTACHMENTS}
            maxRichdownContentSize={MESSAGE_MAX_RICHDOWN_CONTENT_SIZE}
            bottomOverlayHeight={effectiveSharedFooterHeight}
          />
        </div>
        {isShadowViewer && (
          <div ref={sharedFooterContainerRef} className="absolute right-0 bottom-0 left-0 px-4 pb-4">
            <SharedPageFooter
              workspace={currentWorkspace}
              mode={isSignedInShadowViewer ? 'join' : 'auth'}
              onLoginClick={handleLoginClick}
              onJoinClick={handleJoinWorkspaceClick}
            />
          </div>
        )}
      </div>
    </section>
  );

  const mobileHeightClass = withMainLayoutShell ? 'h-full' : 'h-dvh';
  const mobilePanelWidthClass = withMainLayoutShell ? 'w-full' : 'w-dvw';
  const desktopHeightClass = withMainLayoutShell ? 'h-full' : 'h-screen';

  const layoutContent = isBreakpointSm ? (
    <div
      ref={setScrollContainerRef}
      className={cn(
        'scrollbar-hidden relative flex snap-x snap-mandatory overflow-x-auto',
        mobileHeightClass,
        withMainLayoutShell && 'min-h-0 flex-1 rounded-lg',
        withMainLayoutShell && !hasWorkspaceBackgroundImage && 'bg-gray-50 dark:bg-neutral-900',
      )}
    >
      <WorkspaceBackgroundImageLayer style={workspaceBackgroundImageStyle} />
      {renderMainContent(
        cn(
          'relative flex-none snap-start overflow-x-hidden',
          mobileHeightClass,
          mobilePanelWidthClass,
          hasWorkspaceBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME,
        ),
      )}
      <div ref={mobileSidebarPaneRef} className={cn('flex-none snap-start overflow-x-hidden', mobileHeightClass, mobilePanelWidthClass)}>
        {sidebarContent}
      </div>
    </div>
  ) : (
    <div
      className={cn(
        'relative',
        withMainLayoutShell && 'flex min-h-0 flex-1 overflow-hidden rounded-lg',
        withMainLayoutShell && !hasWorkspaceBackgroundImage && 'bg-gray-50 dark:bg-neutral-900',
      )}
    >
      <WorkspaceBackgroundImageLayer style={workspaceBackgroundImageStyle} />
      <div className={cn('flex w-full items-stretch justify-center overflow-hidden', desktopHeightClass)}>
        {/* This outer div is important to horizontal scaling */}
        <div className="flex size-full">
          {renderMainContent(
            cn('relative flex h-full min-w-lg flex-1 flex-col', hasWorkspaceBackgroundImage && WORKSPACE_BACKGROUND_IMAGE_CLASS_NAME),
          )}

          <section
            aria-hidden={!layoutSettings.widgetsOpen}
            inert={layoutSettings.widgetsOpen ? undefined : ''}
            style={{ width: WIDGETS_PANE_WIDTH, marginRight: layoutSettings.widgetsOpen ? 0 : -WIDGETS_PANE_WIDTH }}
            className="relative flex h-full flex-none flex-col overflow-hidden transition-sizes duration-300 ease-fluid"
          >
            {sidebarContent}
          </section>
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      {layoutContent}

      <WorkspacePresenceManager disabled={presenceDisabled} viewingChannelId={currentConfig.channel?.id} />
      <UnreadManager activeWorkspaceId={currentConfig.channel?.workspace_id} activeChannelId={currentConfig.channel?.id} />
    </>
  );

  if (withMainLayoutShell) return content;

  return <KeyMasterProvider>{content}</KeyMasterProvider>;
};

function getPanelIndex(panel) {
  return panel === 'sidebar' ? 1 : 0;
}

const SharedChannelAppShell = ({ workspaceId, shellBootstrap, children }) => {
  const workspaceRailState = useAuthenticatedShellRailState();
  const { userEmail, profile, user } = useCurrentUserShell(workspaceId, shellBootstrap);

  return (
    // Shared pages render through the Rails `fullscreen` layout, not the
    // workspace layout. `MainLayoutFrame` sizes itself with `size-full`, so give
    // it a concrete viewport-height parent here instead of relying on the
    // fullscreen layout's `min-h-dvh` chain.
    <div className="h-dvh w-dvw overflow-hidden">
      <Router>
        <MainLayoutFrame
          workspaceId={workspaceId}
          workspaceRailState={workspaceRailState}
          userEmail={userEmail}
          currentUser={user}
          currentProfile={profile}
          // The shared URL is on a workspace subdomain, but rail/account links
          // belong to the root-domain app. Let those anchors do full browser
          // navigation instead of Wouter handling them as same-origin routes.
          workspaceRailCrossDomainLinks
          // Shared pages render their own session-less shadow BoardView; the
          // persistent pane would mount an interactive board that mints viewer
          // sessions, which shared pages must never do.
          includeWidgetsPane={false}
        >
          {children}
        </MainLayoutFrame>
      </Router>
    </div>
  );
};

const SharedChannelApp = ({ channelId }) => {
  // Workspace id for a shared channel is resolved server-side from the share key
  // and rendered into `current_workspace:id`. Seed the provider from there once
  // at mount; all descendants (including shared components like MessageFeed,
  // Announcement, richdown extensions) read via `useRouteWorkspace()`.
  const workspaceId = useMemo(() => currentWorkspaceId(), []);
  const shadowId = useMemo(() => currentShadowId(), []);
  const userId = useMemo(() => currentUserId(), []);
  // This is shell-only identity. Do not pass it to RouteWorkspaceProvider; the
  // shared room remains shadow-scoped until the user actually joins this space.
  const shellBootstrap = useMemo(() => readSharedShellBootstrap(), []);
  // Signed-in shared viewers should see the same persistent shell and workspace
  // rail as the main app. Anonymous viewers keep the public shared layout and do
  // not boot the main-shell Dexie subscriptions.
  const withMainLayoutShell = useMemo(() => Boolean(userId), [userId]);

  const sharedChannelApp = (
    <RouteWorkspaceProvider workspaceId={workspaceId} requireProfile={false}>
      <SharedChannelAppInner channelId={channelId} shadowId={shadowId} withMainLayoutShell={withMainLayoutShell} />
    </RouteWorkspaceProvider>
  );

  if (!withMainLayoutShell) return sharedChannelApp;

  return (
    <SharedChannelAppShell workspaceId={workspaceId} shellBootstrap={shellBootstrap}>
      {sharedChannelApp}
    </SharedChannelAppShell>
  );
};

export default SharedChannelApp;
